import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AgentAvatar from "../AgentAvatar";
import AgentConfigNav from "./AgentConfigNav";
import {
  getAgentConfigSectionDomId,
} from "./navSectionUtils";

export const AgentConfigSaveButton = styled(Button)(({ theme }) => ({
  borderRadius: "6px",
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  padding: "6px 16px",
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.text.secondary,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
    opacity: 0.4,
  },
}));

export function getAgentConfigSaveButtonSx(saveResult) {
  if (saveResult === "success") {
    return {
      backgroundColor: "success.main",
      "&.Mui-disabled": {
        backgroundColor: "success.main",
        color: "success.contrastText",
        opacity: 1,
      },
    };
  }
  if (saveResult === "error") {
    return {
      backgroundColor: "error.main",
      "&.Mui-disabled": {
        backgroundColor: "error.main",
        color: "error.contrastText",
        opacity: 1,
      },
    };
  }
  return {};
}

export default function AgentConfigDrawerShell({
  presentation = "dialog",
  open,
  onClose,
  saving,
  isCreateMode,
  loaded,
  showCreationPathChooser,
  showManualConfigurationLayout,
  isEditLoading,
  headerTitle,
  headerSubtitle,
  avatarName,
  avatarUrl,
  isSystemAgent,
  isMasterAgent,
  saveErrorMessage,
  handleSave,
  saveButtonDisabled,
  saveResult,
  saveButtonLabel,
  renderCreationPathChooser,
  resetCreationPathSelection,
  children,
  activeTab,
  setActiveTab,
  activeSection,
  onNavigateToSection,
  navGroups,
  agentId,
  renderAiCreationPanel,
}) {
  const isPage = presentation === "page";
  const contentScrollRef = useRef(null);

  useEffect(() => {
    const scrollNode = contentScrollRef.current;
    if (!scrollNode) return undefined;

    if (!activeSection) {
      scrollNode.scrollTo({ top: 0, behavior: "auto" });
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      const sectionNode = document.getElementById(
        getAgentConfigSectionDomId(activeSection)
      );
      if (sectionNode) {
        sectionNode.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [activeTab, activeSection]);

  if (!isPage && !open) {
    return null;
  }

  const shellBody = (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: isPage ? "100%" : "85vh",
          minHeight: 0,
        }}
      >
        {!isPage && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <AgentAvatar
            agentId={agentId}
            name={avatarName}
            src={avatarUrl}
            size={36}
            borderRadius="8px"
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 600, color: "text.primary" }}
            >
              {headerTitle}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "text.disabled" }}>
              {headerSubtitle}
            </Typography>
          </Box>
          {!showCreationPathChooser && !isEditLoading && (
            <AgentConfigSaveButton
              onClick={handleSave}
              disabled={saveButtonDisabled}
              sx={getAgentConfigSaveButtonSx(saveResult)}
            >
              {saveButtonLabel}
            </AgentConfigSaveButton>
          )}
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        )}

        {!isPage && saveErrorMessage && (
          <Box
            sx={{
              mx: 3,
              mt: 2,
              mb: 0.5,
              px: 1.5,
              py: 1.25,
              borderRadius: "8px",
              border: 1,
              borderColor: "error.light",
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.05),
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "error.dark",
                mb: 0.25,
              }}
            >
              Save failed
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "error.dark", lineHeight: 1.35 }}
            >
              {saveErrorMessage}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          {!isCreateMode && !loaded ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : showCreationPathChooser ? (
            renderCreationPathChooser()
          ) : showManualConfigurationLayout ? (
            <>
              <AgentConfigNav
                navGroups={navGroups}
                activeTab={activeTab}
                activeSection={activeSection}
                setActiveTab={setActiveTab}
                onNavigateToSection={onNavigateToSection}
                isSystemAgent={isSystemAgent}
                isMasterAgent={isMasterAgent}
                isCreateMode={isCreateMode}
                agentId={agentId}
                saving={saving}
                showChangeSetupMode={isCreateMode}
                onChangeSetupMode={resetCreationPathSelection}
              />

              <Box
                ref={contentScrollRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: (theme) =>
                    `${theme.palette.divider} transparent`,
                }}
              >
                <Box sx={{ py: 3, px: 3 }}>{children}</Box>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  width: 180,
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: "divider",
                  py: 2,
                  px: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "action.hover",
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  disabled={saving}
                  onClick={resetCreationPathSelection}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: "12px",
                    color: "text.secondary",
                    px: 1,
                  }}
                >
                  Change setup mode
                </Button>
              </Box>
              {renderAiCreationPanel()}
            </Box>
          )}
        </Box>
      </Box>
  );

  if (isPage) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: "100%",
          backgroundColor: "background.paper",
        }}
      >
        {shellBody}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? () => {} : onClose}
      disableEscapeKeyDown={saving}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          border: 1,
          borderColor: "divider",
          maxHeight: "85vh",
          overflow: "hidden",
        },
      }}
    >
      {shellBody}
    </Dialog>
  );
}
